import { DatePicker, Form, FormInstance, Input, Select } from 'antd';
import {
  MainRowColumns,
  PageFilters,
  PhoneFilters,
} from 'components/UI/Responsive/ResponsiveFilters';
import { SaleTypeFilterEnum } from 'enums/saleTypeFilter';
import { ResponsiveLayout } from 'hooks/layout/useResponsiveLayout';
import FormFiltersFields from 'model/FormFiltersFields';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useGetSaleUsersByCustomerIdQuery } from 'services/ecommerceApi';
import { getSelectedCustomerId } from 'store/state/session/sessionSelectors';
import {
  buildMyOrdersFilterChips,
  countActiveAdvancedFilters,
  getClearedFilterValues,
  getMyOrdersFilterInitialValues,
} from './myOrdersFilterValues';

const { Option } = Select;

const MAIN_ROW_COLUMNS: MainRowColumns = {
  tablet: '1fr 1fr',
  table: '1fr 1fr 1fr 1fr',
  desktop: '1fr 1fr 1fr 1fr',
};

interface MyOrdersFilterProps {
  form: FormInstance;
  layout: ResponsiveLayout;
  appliedValues?: Partial<FormFiltersFields>;
  isSheetOpen: boolean;
  onCloseSheet: () => void;
  onSubmit: () => void;
}

export default function MyOrdersFilter(props: MyOrdersFilterProps) {
  const { form, layout, appliedValues, isSheetOpen, onCloseSheet, onSubmit } =
    props;
  const { t: translate } = useTranslation();
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [activeAdvancedCount, setActiveAdvancedCount] = useState(0);
  const selectedCustomerId = useSelector(getSelectedCustomerId);
  const isPhone = layout === 'phone';

  const { data: users, refetch: refetchUsers } =
    useGetSaleUsersByCustomerIdQuery(
      {
        customerId: selectedCustomerId,
      },
      { skip: !selectedCustomerId },
    );

  useEffect(() => {
    refetchUsers();
  }, [selectedCustomerId]);

  useEffect(() => {
    form.setFieldsValue({
      'Data.UserName': '',
    });
    setActiveAdvancedCount(countActiveAdvancedFilters(form.getFieldsValue()));
  }, [selectedCustomerId, form]);

  const refreshAdvancedCount = () =>
    setActiveAdvancedCount(countActiveAdvancedFilters(form.getFieldsValue()));

  const onClear = () => {
    form.setFieldsValue(getClearedFilterValues());
    refreshAdvancedCount();
  };

  const onSearch = () => form.submit();

  const onSearchAndClose = () => {
    form.submit();
    onCloseSheet();
    if (layout === 'tablet') setIsAdvancedOpen(false);
  };

  const onRemoveChip = (field: keyof FormFiltersFields) => {
    const cleared = getClearedFilterValues() as Record<string, unknown>;
    form.setFieldsValue({ [field]: cleared[field] });
    refreshAdvancedCount();
    form.submit();
  };

  const mainFields = (
    <>
      <Form.Item
        label={translate('myOrders.orderType.formFilter.saleDate')}
        name="Data.Date"
      >
        <DatePicker format="DD/MM/YYYY" inputReadOnly={isPhone} />
      </Form.Item>

      <Form.Item
        label={translate('myOrders.orderType.formFilter.filterDateBy')}
        name="Data.TypeDate"
      >
        <Select>
          <Option key="buyDate" value="buyDate">
            {translate('myOrders.orderType.formFilter.buyDate')}
          </Option>
          <Option key="deliveryDate" value="deliveryDate">
            {translate('myOrders.orderType.formFilter.deliveryDate')}
          </Option>
          <Option key="billingDate" value="billingDate">
            {translate('myOrders.orderType.formFilter.fatDate')}
          </Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={translate('myOrders.orderType.formFilter.saleType')}
        name="Data.SaleTypes"
      >
        <Select>
          <Option key={SaleTypeFilterEnum.ALL} value={SaleTypeFilterEnum.ALL}>
            {translate('myOrders.orderType.formFilter.all')}
          </Option>
          <Option
            key={SaleTypeFilterEnum.DIRECTED}
            value={SaleTypeFilterEnum.DIRECTED}
          >
            {translate('myOrders.orderType.formFilter.directed')}
          </Option>
          <Option key={SaleTypeFilterEnum.VOL} value={SaleTypeFilterEnum.VOL}>
            {translate('myOrders.orderType.formFilter.vol')}
          </Option>
          <Option key={SaleTypeFilterEnum.LKP} value={SaleTypeFilterEnum.LKP}>
            {translate('myOrders.orderType.formFilter.lkp')}
          </Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={translate('myOrders.orderType.formFilter.status')}
        name="Data.Status"
      >
        <Select allowClear>
          <Option key="all" value="">
            {translate('myOrders.orderType.formFilter.all')}
          </Option>
          <Option key="F" value="F">
            {translate('myOrders.orderType.formFilter.generate')}
          </Option>
          <Option key="E" value="E">
            {translate('myOrders.orderType.formFilter.notGenerate')}
          </Option>
          <Option key="A" value="A">
            {translate('myOrders.orderType.formFilter.open')}
          </Option>
          <Option key="R" value="R">
            {translate('myOrders.orderType.formFilter.process')}
          </Option>
          <Option key="P" value="P">
            {translate('myOrders.orderType.formFilter.programmed')}
          </Option>
          <Option key="C" value="C">
            {translate('myOrders.orderType.formFilter.canceled')}
          </Option>
        </Select>
      </Form.Item>
    </>
  );

  const advancedFields = (
    <>
      <Form.Item
        label={translate('myOrders.orderType.formFilter.orderNumber')}
        name="Data.OrderNumber"
      >
        <Input type="text" inputMode="numeric" />
      </Form.Item>
      <Form.Item
        label={translate('myOrders.orderType.formFilter.producer')}
        name="Data.SiteTradingName"
      >
        <Input type="text" />
      </Form.Item>
      <Form.Item
        label={translate('myOrders.orderType.formFilter.productDescription')}
        name="Data.ProductDescription"
      >
        <Input type="text" />
      </Form.Item>
      <Form.Item
        label={translate('myOrders.orderType.formFilter.accounts')}
        name="Data.UserName"
      >
        <Select allowClear>
          {users?.map((user) => (
            <Option key={user} value={user}>
              {user}
            </Option>
          ))}
        </Select>
      </Form.Item>
    </>
  );

  const layoutProps = {
    mainFields,
    advancedFields,
    activeAdvancedCount,
    isAdvancedOpen,
    onToggleAdvanced: () => setIsAdvancedOpen((isShown) => !isShown),
    onClear,
    onSearchAndClose,
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSubmit}
      onValuesChange={refreshAdvancedCount}
      initialValues={getMyOrdersFilterInitialValues()}
      data-testid="my-orders-filters"
    >
      {layout === 'phone' ? (
        <PhoneFilters
          {...layoutProps}
          chips={
            appliedValues
              ? buildMyOrdersFilterChips(appliedValues, onRemoveChip)
              : []
          }
          isSheetOpen={isSheetOpen}
          onCloseSheet={onCloseSheet}
        />
      ) : (
        <PageFilters
          {...layoutProps}
          layout={layout}
          mainRowColumns={MAIN_ROW_COLUMNS}
          onSearch={onSearch}
        />
      )}
    </Form>
  );
}
